import { fetchWithAuth } from "./http";

// ── Response types ──────────────────────────────────────────────────────────

export interface ComponentHealth {
  name: string;
  status: "healthy" | "degraded" | "down" | "unconfigured";
  latency_ms: number | null;
  detail: string | null;
}

export interface HealthResponse {
  overall_status: string;
  components: ComponentHealth[];
  checked_at: string;
}

export interface SyncRun {
  sync_time: string;
  success: boolean;
  records_inserted: number | null;
  records_updated: number | null;
  respondents_failed: number | null;
  error_message: string | null;
}

export interface StaleCheckpoint {
  ba_code: string;
  last_sync: string | null;
  hours_stale: number | null;
}

export interface SourceSyncSummary {
  syncs: number;
  errors: number;
}

// A respondent currently mid a repeated-failure streak, sourced from
// sync_checkpoints.consecutive_failures. Distinct from stalest_regions:
// this is "failing right now, N runs in a row," not merely stale data.
export interface FailingRespondent {
  data_source: string;
  data_stream: string;
  respondent_code: string;
  consecutive_failures: number;
  failing_since: string | null;
  error_message: string | null;
}

export interface GridSyncResponse {
  status: string;
  last_successful_sync: string | null;
  syncs_24h: number;
  errors_24h: number;
  recent_syncs: SyncRun[];
  stalest_regions: StaleCheckpoint[];
  failing_respondents: FailingRespondent[];
  generation_row_count: number | null;
  generation_latest: string | null;
  source_breakdown: Record<string, SourceSyncSummary>;
  /** Sub-queries that failed and were degraded to their zero-value default. */
  errors: string[];
  checked_at: string;
}

export interface BACoverage {
  ba_code: string;
  hours_with_data: number;
  completeness_pct: number | null;
  latest_computed: string | null;
}

export interface RecentGap {
  hour: string;
  missing_bas: string[];
}

export interface GridImpactResponse {
  status: string;
  total_records: number;
  distinct_bas: number;
  earliest_hour: string | null;
  latest_hour: string | null;
  latest_computed_at: string | null;
  ba_coverage: BACoverage[];
  recent_gaps: RecentGap[];
  errors: string[];
  checked_at: string;
}

export interface ActiveTask {
  task_id: string;
  task_name: string | null;
  started: string | null;
  args: string | null;
}

export interface RecentModelRun {
  run_id: string;
  project_name: string | null;
  status: string;
  created_at: string | null;
  completed_at: string | null;
}

export interface TaskMonitoringResponse {
  worker_count: number;
  active_tasks: ActiveTask[];
  reserved_count: number;
  recent_runs: RecentModelRun[];
  runs_today: number;
  failures_today: number;
  errors: string[];
  checked_at: string;
}

// ── API functions ───────────────────────────────────────────────────────────

export const getSystemHealth = (token: string) =>
  fetchWithAuth<HealthResponse>("/admin/monitoring/health", token);

export const getGridSyncStatus = (token: string) =>
  fetchWithAuth<GridSyncResponse>("/admin/monitoring/grid-sync", token);

export const getGridImpactCoverage = (token: string) =>
  fetchWithAuth<GridImpactResponse>("/admin/monitoring/grid-impacts", token);

export const getTaskMonitoring = (token: string) =>
  fetchWithAuth<TaskMonitoringResponse>("/admin/monitoring/tasks", token);

// ── Heatmap types ──────────────────────────────────────────────────────────

export interface DailyBAStatus {
  date: string;
  ba_code: string;
  hours_with_data: number;
  hours_expected: number;
  completeness: number;
}

export interface SyncHeatmapResponse {
  days: number;
  source: string;
  data: DailyBAStatus[];
  ba_codes: string[];
  date_range: string[];
  checked_at: string;
}

export type GridSource = "eia" | "entsoe" | "bmrs" | "lmp" | "all";

// ── Multi-resolution heatmap types ────────────────────────────────────────

export interface PeriodBAStatus {
  period: string;
  ba_code: string;
  hours_with_data: number;
  hours_expected: number;
  completeness: number;
}

export interface HeatmapBand {
  resolution: "yearly" | "monthly" | "daily";
  data: PeriodBAStatus[];
  periods: string[];
}

export interface HeatmapHistoryResponse {
  source: string;
  bands: HeatmapBand[];
  ba_codes: string[];
  errors: string[];
  checked_at: string;
}

export interface SyncSourceInfo {
  name: string;
  table_exists: boolean;
  row_count: number;
  latest_timestamp: string | null;
}

export interface SyncSourcesResponse {
  sources: SyncSourceInfo[];
  errors: string[];
  checked_at: string;
}

export const getSyncHeatmap = (
  token: string,
  days: number = 30,
  source: GridSource = "eia",
) =>
  fetchWithAuth<SyncHeatmapResponse>(
    `/admin/monitoring/grid-sync/heatmap?days=${days}&source=${source}`,
    token,
  );

export const getHeatmapHistory = (
  token: string,
  source: GridSource = "eia",
) =>
  fetchWithAuth<HeatmapHistoryResponse>(
    `/admin/monitoring/grid-sync/heatmap/history?source=${source}`,
    token,
  );

export const getSyncSources = (token: string) =>
  fetchWithAuth<SyncSourcesResponse>(
    "/admin/monitoring/grid-sync/sources",
    token,
  );
